// XQ's `Binary` in TypeScript: a value in the binary form its type gives, and back, by the type's
// schema, which the compiler writes (`Binary.schema<T>()`) and the server hands out — so the browser
// reads and writes the server's messages tag for tag, with no copy of the protocol kept by hand.
//
// The form: an int is a zigzag LEB128 varint, a float eight bytes (little-endian), a bool a byte, a
// string its length in bytes and UTF-8; an array, a set and a map their count and elements (a map's
// key and value in turn); a tuple and a record (fields in their order) their parts; a union a tag
// (the member's number) and the member — a union of one member is that member; a typed array its
// count and bytes; null and literals take no bytes. Ints are exact within ±2^53, as JavaScript
// numbers are; a record is an object, a set a `Set`, a map a `Map`, a typed array of its kind.

/** A schema, as JSON gives it. */
export type Schema =
  | "int"
  | "float"
  | "bool"
  | "string"
  | "null"
  | { literal: unknown }
  | { record: [string, Schema][] }
  | { tuple: Schema[] }
  | { array: Schema }
  | { set: Schema }
  | { map: [Schema, Schema] }
  | { typed: keyof typeof TYPED }
  | { union: Schema[] }
  | { alias: number; type: Schema }
  | { ref: number };

/** Writes and reads values of type `T`. */
export type Codec<T> = { encode(value: T): Uint8Array<ArrayBuffer>; decode(bytes: Uint8Array): T };

/** A codec of the type of schema `schema` (parsed JSON): `encode(value)` gives a `Uint8Array`, and
 * `decode(bytes)` the value or throws an `Error` that says what is wrong and where. */
export function codec<T>(schema: Schema): Codec<T> {
  const t = compile(schema, []);
  return {
    encode(value: T): Uint8Array<ArrayBuffer> {
      const w = new Writer();
      t.put(w, value);
      return w.bytes();
    },
    decode(bytes: Uint8Array): T {
      const r = new Reader(bytes);
      const v = t.get(r);
      if (r.pos !== bytes.length) r.fail(`${bytes.length - r.pos} bytes left over`);
      return v as T;
    },
  };
}

const TYPED = {
  u8: Uint8Array,
  i8: Int8Array,
  u16: Uint16Array,
  i16: Int16Array,
  u32: Uint32Array,
  i32: Int32Array,
  f32: Float32Array,
  f64: Float64Array,
};

/** The parts of a codec: `put(writer, value)`, `get(reader)`, and `is(value)` — whether a value is of
 * the type, which picks the member of a union a value is; a literal's value; the bytes an element takes
 * at least, if fixed. */
type Part = {
  put(w: Writer, v: any): void;
  get(r: Reader): unknown;
  is(v: unknown): boolean;
  literal?: unknown;
  fixed?: number;
};

/** The codec's parts of `s`; `aliases` are the recursive aliases around it, by number. */
function compile(s: Schema, aliases: Part[]): Part {
  if (typeof s === "string") return SCALARS[s] ?? bad(s);
  if ("literal" in s) {
    const v = s.literal;
    return { literal: v, put() {}, get: () => v, is: (x) => x === v };
  }
  if ("record" in s) {
    const fields = s.record.map(([name, f]): [string, Part] => [name, compile(f, aliases)]);
    const literal = fields.filter(([, f]) => f.literal !== undefined);
    return {
      put(w, v) {
        for (const [name, f] of fields) f.put(w, v[name] ?? null);
      },
      get(r) {
        const o: Record<string, unknown> = {};
        for (const [name, f] of fields) o[name] = f.get(r);
        return o;
      },
      is: (v) =>
        v !== null &&
        typeof v === "object" &&
        !Array.isArray(v) &&
        literal.every(([name, f]) => f.is((v as Record<string, unknown>)[name])) &&
        fields.every(([name, f]) => name in v || f.is(null)),
    };
  }
  if ("tuple" in s) {
    const parts = s.tuple.map((p) => compile(p, aliases));
    return {
      put(w, v) {
        parts.forEach((p, i) => p.put(w, v[i]));
      },
      get: (r) => parts.map((p) => p.get(r)),
      is: (v) => Array.isArray(v) && v.length === parts.length && parts.every((p, i) => p.is(v[i])),
    };
  }
  if ("array" in s || "set" in s) {
    const set = "set" in s;
    const e = compile("set" in s ? s.set : s.array, aliases);
    return {
      put(w, v) {
        w.varint(set ? v.size : v.length);
        for (const x of v) e.put(w, x);
      },
      get(r) {
        const n = r.count(e.fixed ?? 1);
        const out = new Array<unknown>(n);
        for (let i = 0; i < n; i++) out[i] = e.get(r);
        return set ? new Set(out) : out;
      },
      is: (v) => (set ? v instanceof Set : Array.isArray(v)),
    };
  }
  if ("map" in s) {
    const [k, val] = s.map.map((p) => compile(p, aliases));
    return {
      put(w, v) {
        w.varint(v.size);
        for (const [a, b] of v) {
          k.put(w, a);
          val.put(w, b);
        }
      },
      get(r) {
        const n = r.count(1);
        const m = new Map<unknown, unknown>();
        for (let i = 0; i < n; i++) m.set(k.get(r), val.get(r));
        return m;
      },
      is: (v) => v instanceof Map,
    };
  }
  if ("typed" in s) {
    const Kind = TYPED[s.typed] ?? bad(s);
    return {
      put(w, v) {
        w.varint(v.length);
        w.raw(new Uint8Array(v.buffer, v.byteOffset, v.byteLength));
      },
      get(r) {
        const n = r.count(Kind.BYTES_PER_ELEMENT);
        const b = r.take(n * Kind.BYTES_PER_ELEMENT);
        // A copy: its own buffer, aligned for its elements.
        return new Kind(b.slice().buffer);
      },
      is: (v) => v instanceof Kind,
    };
  }
  if ("union" in s) {
    const members = s.union.map((m) => compile(m, aliases));
    return {
      put(w, v) {
        const i = members.findIndex((m) => m.is(v));
        if (i < 0) throw new Error(`Binary: ${JSON.stringify(v)} is of no member of the union`);
        w.varint(i);
        members[i].put(w, v);
      },
      get(r) {
        const i = r.varint();
        if (i >= members.length) r.fail(`tag ${i} is not of the ${members.length} members of the union`);
        return members[i].get(r);
      },
      is: (v) => members.some((m) => m.is(v)),
    };
  }
  if ("alias" in s) {
    // Filled in below: the type refers to itself through it.
    const self = {} as Part;
    const inner = [...aliases];
    inner[s.alias] = self;
    Object.assign(self, compile(s.type, inner));
    return self;
  }
  if ("ref" in s) {
    const target = aliases[s.ref] ?? bad(s);
    return { put: (w, v) => target.put(w, v), get: (r) => target.get(r), is: (v) => target.is(v) };
  }
  return bad(s);
}

function bad(s: unknown): never {
  throw new Error(`Binary: not a schema: ${JSON.stringify(s)}`);
}

const SCALARS: Record<string, Part> = {
  int: {
    put(w, v) {
      if (!Number.isInteger(v)) throw new Error(`Binary: ${v} is not an int`);
      w.varint(v >= 0 ? v * 2 : -v * 2 - 1);
    },
    get(r) {
      const z = r.varint();
      return z % 2 === 0 ? z / 2 : -(z + 1) / 2;
    },
    is: (v) => Number.isInteger(v),
  },
  float: {
    put: (w, v) => w.float(v),
    get: (r) => r.float(),
    is: (v) => typeof v === "number",
    fixed: 8,
  },
  bool: {
    put: (w, v) => w.byte(v ? 1 : 0),
    get(r) {
      const b = r.byte();
      if (b > 1) r.fail(`a bool of ${b}`);
      return b === 1;
    },
    is: (v) => typeof v === "boolean",
  },
  string: {
    put(w, v) {
      const b = ENCODER.encode(v);
      w.varint(b.length);
      w.raw(b);
    },
    get(r) {
      return DECODER.decode(r.take(r.count(1)));
    },
    is: (v) => typeof v === "string",
  },
  null: { put() {}, get: () => null, is: (v) => v === null || v === undefined },
};

const ENCODER = new TextEncoder();
const DECODER = new TextDecoder("utf-8", { fatal: true });

class Writer {
  buf: Uint8Array<ArrayBuffer> = new Uint8Array(256);
  pos = 0;
  view = new DataView(this.buf.buffer);

  room(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    const b = new Uint8Array(Math.max(this.buf.length * 2, this.pos + n));
    b.set(this.buf.subarray(0, this.pos));
    this.buf = b;
    this.view = new DataView(b.buffer);
  }

  byte(b: number): void {
    this.room(1);
    this.buf[this.pos++] = b;
  }

  /** An unsigned varint of `z` (exact within 2^53). */
  varint(z: number): void {
    this.room(10);
    while (z >= 128) {
      this.buf[this.pos++] = (z % 128) | 128;
      z = Math.floor(z / 128);
    }
    this.buf[this.pos++] = z;
  }

  float(v: number): void {
    this.room(8);
    this.view.setFloat64(this.pos, v, true);
    this.pos += 8;
  }

  raw(b: Uint8Array): void {
    this.room(b.length);
    this.buf.set(b, this.pos);
    this.pos += b.length;
  }

  bytes(): Uint8Array<ArrayBuffer> {
    return this.buf.slice(0, this.pos);
  }
}

class Reader {
  pos = 0;
  private b: Uint8Array;
  private view: DataView;

  constructor(b: Uint8Array) {
    this.b = b;
    this.view = new DataView(b.buffer, b.byteOffset, b.byteLength);
  }

  fail(what: string): never {
    throw new Error(`Binary: ${what} (at byte ${this.pos})`);
  }

  byte(): number {
    if (this.pos >= this.b.length) this.fail("the data ends");
    return this.b[this.pos++];
  }

  varint(): number {
    let z = 0;
    let mul = 1;
    for (let i = 0; i < 10; i++) {
      const b = this.byte();
      z += (b & 127) * mul;
      if (b < 128) return z;
      mul *= 128;
    }
    this.fail("a varint longer than 64 bits");
  }

  /** A count of things of at least `least` bytes each, checked against the bytes left. */
  count(least: number): number {
    const n = this.varint();
    if (n * least > this.b.length - this.pos) this.fail(`a count of ${n} past the end`);
    return n;
  }

  float(): number {
    if (this.pos + 8 > this.b.length) this.fail("the data ends");
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }

  take(n: number): Uint8Array {
    if (this.pos + n > this.b.length) this.fail("the data ends");
    const b = this.b.subarray(this.pos, this.pos + n);
    this.pos += n;
    return b;
  }
}
